import { useState } from 'react';
import { taskSettingsInputSchema, type TaskDetail, type TaskSettingsInput } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { formatDayYear } from '../../lib/project-display';
import { PRIORITY_OPTIONS } from '../../lib/task-display';
import { useUpdateTaskSettings } from '../../lib/tasks-api';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog } from '../ui/dialog';
import { DateField } from '../ui/date-field';
import { NumberStepper } from '../ui/number-stepper';
import { Select } from '../ui/select';
import { textLinkClass } from '../ui/text-link';
import { Toggle } from '../ui/toggle';
import { useToast } from '../ui/toast';

const PRIORITY_LABEL = Object.fromEntries(PRIORITY_OPTIONS.map((o) => [o.value, o.label])) as Record<string, string>;
const dayOnly = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

function Fact({ label, children, muted }: { label: string; children: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-(--mbm-space-4)">
      <dt className="text-body-small text-text-secondary">{label}</dt>
      <dd className={muted ? 'text-body-small text-text-tertiary' : 'text-label-medium text-text-primary'}>
        {children}
      </dd>
    </div>
  );
}

function SwitchRow({
  title,
  help,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-(--mbm-space-4)">
      <div className="flex min-w-0 flex-col">
        <span className="text-label-medium text-text-primary">{title}</span>
        <span className="text-body-xsmall text-text-tertiary">{help}</span>
      </div>
      <Toggle aria-label={title} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

function EditDetailsDialog({ task, open, onClose }: { task: TaskDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateTaskSettings(task.id);
  const [values, setValues] = useState({
    priority: task.priority,
    workUnits: task.workUnits,
    dueDate: dayOnly(task.deadline),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  const submit = async () => {
    setFormError(undefined);
    // Only what moved is sent, so a task whose due date has already passed can still change its priority.
    const body: TaskSettingsInput = {
      ...(values.priority !== task.priority ? { priority: values.priority } : {}),
      ...(values.workUnits !== task.workUnits ? { workUnits: values.workUnits } : {}),
      ...(values.dueDate !== dayOnly(task.deadline) ? { dueDate: values.dueDate || null } : {}),
    };
    if (Object.keys(body).length === 0) return onClose();
    const parsed = taskSettingsInputSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      await update.mutateAsync(body);
      toast({ title: 'Details saved' });
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length)
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
      else if (e.kind === 'forbidden') setFormError('Only the account manager or an admin can change these.');
      else if (e.status === 409) setFormError(e.message ?? 'This task can’t be changed right now.');
      else setFormError('We couldn’t save the changes. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit details"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      {formError ? <Banner tone="error">{formError}</Banner> : null}
      <Select
        label="Priority"
        variant="filled"
        value={values.priority}
        error={errors.priority}
        onChange={(e) => setValues((v) => ({ ...v, priority: e.target.value as typeof v.priority }))}
      >
        {PRIORITY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      <DateField
        label="Due for review"
        value={values.dueDate}
        error={errors.dueDate}
        helperText="Clear it to remove the deadline."
        onChange={(dueDate) => setValues((v) => ({ ...v, dueDate }))}
      />
      <NumberStepper
        label="Effort (units)"
        helperText="1 unit = 2 h."
        value={values.workUnits}
        min={1}
        max={99}
        onChange={(workUnits) => setValues((v) => ({ ...v, workUnits }))}
      />
    </Dialog>
  );
}

/**
 * Figma A05.4 "Details": the facts about the task and the two client-facing switches. The account manager and admins
 * can change them; everyone else reads them.
 * TODO(api): "Publishes", "Allowance" (plan quota) and "Deliverables" have no data yet: the review step, billing and
 * deliverables steps bring them. Drawn as the design has them, with nothing to say.
 */
export function TaskDetailsCard({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const update = useUpdateTaskSettings(task.id);
  const [editing, setEditing] = useState(false);
  const canManage = task.access.manage && task.status !== 'Cancelled' && task.status !== 'User Draft';
  const flip = (patch: TaskSettingsInput) =>
    update.mutate(patch, {
      onError: () => toast({ title: 'Couldn’t save that change', description: 'Please try again.', tone: 'error' }),
    });

  return (
    <Card tone="panel" className="flex flex-col gap-(--mbm-space-4)">
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">Details</h2>
        {canManage ? (
          <button type="button" onClick={() => setEditing(true)} className={textLinkClass}>
            Edit
          </button>
        ) : null}
      </div>
      <dl className="flex flex-col gap-(--mbm-space-3) border-b border-border-line pb-(--mbm-space-4)">
        <Fact label="Priority">{PRIORITY_LABEL[task.priority] ?? task.priority}</Fact>
        <Fact label="Due for review">{task.deadline ? formatDayYear(task.deadline) : 'Not set'}</Fact>
        <Fact label="Publishes" muted>
          —
        </Fact>
        <Fact label="Effort">
          {task.workUnits} {task.workUnits === 1 ? 'unit' : 'units'}
        </Fact>
        <Fact label="Allowance" muted>
          —
        </Fact>
        <Fact label="Deliverables" muted>
          None yet
        </Fact>
      </dl>
      <SwitchRow
        title="Visible to client"
        help="Shown in their Work board"
        checked={task.clientVisible}
        disabled={!canManage || update.isPending}
        onChange={(clientVisible) => flip({ clientVisible })}
      />
      <SwitchRow
        title="Needs client approval"
        help="Turn off to publish straight after lead QA"
        checked={task.requiresClientApproval}
        disabled={!canManage || update.isPending}
        onChange={(requiresClientApproval) => flip({ requiresClientApproval })}
      />
      {canManage ? (
        <EditDetailsDialog key={String(editing)} task={task} open={editing} onClose={() => setEditing(false)} />
      ) : null}
    </Card>
  );
}
